import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import DLOModal from './DLOModal';
import { RootState } from '../../../store/index';
import { hideErrorModal, removeErrorById } from '../../../store/slices/dloSlice';

/**
 * Convert API name to user-friendly title format
 * Example: 'fetchDashboardSchedule' -> 'Fetch Dashboard Schedule Failed'
 */
const getApiTitle = (apiName: string): string => {
  // Convert camelCase to words and capitalize
  const words = apiName
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
  
  return `${words} Failed`;
};

/**
 * Centralized Error Modal for DLO Operations
 * 
 * This component displays API errors in a user-friendly way using the DLOModal component.
 * It automatically appears when an API error occurs and provides options to:
 * - Try again (closes modal)
 * - Contact support (placeholder for future implementation)
 * - Dismiss the error
 * 
 * Features:
 * - Consistent error display across all DLO components
 * - User-friendly error messages
 * - Error tracking and logging
 * - Responsive design
 */
export const DLOErrorModal: React.FC = () => {
  const dispatch = useDispatch();
  const { activeError, isErrorModalVisible } = useSelector(
    (state: RootState) => state.dlo.centralizedErrors
  );

  const handleClose = () => {
    if (activeError) {
      // Remove the error from the store and close modal
      dispatch(removeErrorById(activeError.id));
    }
    dispatch(hideErrorModal());
  };

  if (!activeError) {
    return null;
  }

  return (
    <DLOModal
      visible={isErrorModalVisible}
      onClose={handleClose}
      title={getApiTitle(activeError.apiName)}
      primaryButtonText="OK"
      secondaryButtonText=""
      onPrimaryPress={handleClose}
      hideButtons={false}
      showCloseIcon={true}
    >
      <Text style={styles.modalMessage}>{activeError.message}</Text>
    </DLOModal>
  );
};

const styles = StyleSheet.create({
  modalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOErrorModal;